import { useState } from 'react';
import { X, Plus, Trash2, CheckCircle, AlertCircle, Code } from 'lucide-react';

const SQL_KEYWORDS = new Set([
  'SELECT','WHERE','ON','SET','VALUES','INTO','AND','OR','NOT','NULL','AS','BY',
  'GROUP','ORDER','HAVING','LIMIT','OFFSET','UNION','ALL','DISTINCT','CASE','WHEN',
  'THEN','ELSE','END','WITH','EXISTS','IN','BETWEEN','LIKE','IS','INNER','LEFT',
  'RIGHT','FULL','CROSS','OUTER',
]);

const lastSegment = (ref) => ref.split('.').pop();

function parseQueries(validQueries) {
  const tableSet = new Set();
  const relationships = [];

  for (const sql of validQueries) {
    const aliasMap = {};
    const aliasRe = /(?:FROM|JOIN)\s+((?:\w+\.)*\w+)\s+(?:AS\s+)?(\w+)(?=\s)/gi;
    let am;
    while ((am = aliasRe.exec(sql)) !== null) {
      const tableName = lastSegment(am[1]);
      const alias = am[2];
      if (!SQL_KEYWORDS.has(alias.toUpperCase()) && alias.toUpperCase() !== tableName.toUpperCase()) {
        aliasMap[alias] = tableName;
      }
    }
    const resolveAlias = (name) => aliasMap[name] ?? aliasMap[name.toLowerCase()] ?? name;

    const fromRe = /(?:FROM|JOIN)\s+((?:\w+\.)*\w+)/gi;
    let fm;
    while ((fm = fromRe.exec(sql)) !== null) {
      const name = lastSegment(fm[1]);
      if (!SQL_KEYWORDS.has(name.toUpperCase())) tableSet.add(name);
    }

    const joinRe = /JOIN\s+((?:\w+\.)*\w+)(?:\s+(?:AS\s+)?(\w+))?\s+ON\s+((?:\w+\.)*\w+)\s*=\s*((?:\w+\.)*\w+)/gi;
    let jm;
    while ((jm = joinRe.exec(sql)) !== null) {
      const toTable = lastSegment(jm[1]);
      const joinAlias = jm[2];
      if (joinAlias) aliasMap[joinAlias] = toTable;
      const leftAlias = jm[3].includes('.') ? jm[3].split('.')[0] : jm[3];
      const fromTable = resolveAlias(leftAlias);
      const fromCol = lastSegment(jm[3]);
      const toCol = lastSegment(jm[4]);
      const joinType = /\bLEFT\b/i.test(sql) ? 'LEFT JOIN' : /\bRIGHT\b/i.test(sql) ? 'RIGHT JOIN' : 'INNER JOIN';
      if (!SQL_KEYWORDS.has(fromTable.toUpperCase()) && !SQL_KEYWORDS.has(toTable.toUpperCase())) {
        relationships.push({ from_table: fromTable, to_table: toTable, join_type: joinType, join_columns: [`${fromCol} = ${toCol}`], frequency: 1, confidence: 0.9 });
      }
    }
  }

  return {
    tables: Array.from(tableSet).map(t => ({ table_name: t, schema: '', usage_count: 1, columns_referenced: [] })),
    relationships,
    summary: `Analyzed ${validQueries.length} ${validQueries.length === 1 ? 'query' : 'queries'}. Found ${tableSet.size} unique tables${relationships.length > 0 ? ` and ${relationships.length} join relationship${relationships.length !== 1 ? 's' : ''}` : ''}.`,
    queries_analyzed: validQueries.length,
  };
}

export default function QueryContextManager({ onClose, onSave, existingContexts = [] }) {
  const [contexts, setContexts] = useState(existingContexts);
  const [bulkQuery, setBulkQuery] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const addContext = () => {
    setContexts(prev => [...prev, { query: '', purpose: '', frequency: 'medium', business_area: '' }]);
    setShowBulkInput(false);
  };

  const updateContext = (index, field, value) => {
    setContexts(prev => prev.map((c, i) => i === index ? { ...c, [field]: value } : c));
  };

  const removeContext = (index) => {
    setContexts(prev => prev.filter((_, i) => i !== index));
  };

  const parseBulkQueries = () => {
    const queries = bulkQuery.split(';').map(q => q.trim()).filter(q => q.length > 0);
    setContexts(prev => [...prev, ...queries.map(query => ({ query, purpose: '', frequency: 'medium', business_area: '' }))]);
    setBulkQuery('');
    setShowBulkInput(false);
  };

  const analyzeQueries = async () => {
    setAnalyzing(true);
    setError(null);
    try {
      const validQueries = contexts.map(c => c.query).filter(q => q.trim().length > 0);
      setAnalysisResult(parseQueries(validQueries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = () => {
    onSave(contexts.filter(c => c.query.trim().length > 0));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-5xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Query Context Manager</h2>
            <p className="text-sm text-slate-600 mt-1">
              Add SQL queries to improve relationship detection and subdomain grouping
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="h-5 w-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Bulk Input */}
          {showBulkInput && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">
                Paste Multiple Queries (semicolon-separated)
              </label>
              <textarea
                value={bulkQuery}
                onChange={e => setBulkQuery(e.target.value)}
                placeholder={"SELECT * FROM customers c JOIN orders o ON c.customer_id = o.customer_id;\nSELECT * FROM orders WHERE status = 'completed';"}
                className="w-full h-32 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
              />
              <button
                onClick={parseBulkQueries}
                disabled={!bulkQuery.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Parse Queries
              </button>
            </div>
          )}

          {/* Individual Contexts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Query Contexts ({contexts.length})</h3>
              <button
                onClick={addContext}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-primary hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Plus className="h-4 w-4" /> Add Query
              </button>
            </div>

            {contexts.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Code className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No queries added yet</p>
                <p className="text-sm mt-1">Add queries using bulk input or click &quot;Add Query&quot;</p>
              </div>
            ) : (
              contexts.map((context, index) => (
                <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <span className="text-xs font-semibold text-slate-500">Query {index + 1}</span>
                    <button onClick={() => removeContext(index)} className="p-1 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                  <textarea
                    value={context.query}
                    onChange={e => updateContext(index, 'query', e.target.value)}
                    placeholder="SELECT * FROM table1 JOIN table2 ON ..."
                    className="w-full h-20 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-xs"
                  />
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      value={context.purpose || ''}
                      onChange={e => updateContext(index, 'purpose', e.target.value)}
                      placeholder="Purpose (optional)"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                    <select
                      value={context.frequency || 'medium'}
                      onChange={e => updateContext(index, 'frequency', e.target.value)}
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    >
                      <option value="high">High Frequency</option>
                      <option value="medium">Medium Frequency</option>
                      <option value="low">Low Frequency</option>
                    </select>
                    <input
                      value={context.business_area || ''}
                      onChange={e => updateContext(index, 'business_area', e.target.value)}
                      placeholder="Business Area"
                      className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Analysis Results */}
          {analysisResult && (
            <div className="border border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <h4 className="font-semibold text-green-900">Analysis Complete</h4>
              </div>
              <p className="text-sm text-green-800 mb-3">{analysisResult.summary}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-2">Tables Found ({analysisResult.tables.length})</p>
                  <div className="space-y-1">
                    {analysisResult.tables.map((table, i) => (
                      <div key={i} className="text-xs text-green-800 bg-white px-2 py-1 rounded">{table.table_name} ({table.usage_count}x)</div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-2">Relationships ({analysisResult.relationships.length})</p>
                  <div className="space-y-1">
                    {analysisResult.relationships.map((rel, i) => (
                      <div key={i} className="text-xs text-green-800 bg-white px-2 py-1 rounded">{rel.from_table} → {rel.to_table}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50">
          <button
            onClick={analyzeQueries}
            disabled={analyzing || contexts.length === 0}
            className="px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {analyzing ? 'Analyzing...' : 'Preview Analysis'}
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primaryDark text-sm font-medium">
              Save & Use
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
